/**
 * /staff/accept-invite/:token — public
 *
 * Verifies an internal-staff invite token, lets the invitee set a password,
 * then routes to /staff/login.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import api from "@/lib/api";
import { ShieldCheck } from "@phosphor-icons/react";

export default function StaffAcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();

  const [invite, setInvite] = useState(null);
  const [error, setError]   = useState(null);
  const [pwd1, setPwd1]     = useState("");
  const [pwd2, setPwd2]     = useState("");
  const [busy, setBusy]     = useState(false);
  const [done, setDone]     = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/internal/invite/${token}`);
        setInvite(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Invalid or expired invitation");
      }
    })();
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (pwd1.length < 10) { setError("Password must be at least 10 characters"); return; }
    if (pwd1 !== pwd2) { setError("Passwords do not match"); return; }
    setError(null); setBusy(true);
    try {
      await api.post(`/internal/invite/${token}/accept`, { password: pwd1 });
      setDone(true);
      setTimeout(() => nav("/staff/login"), 1200);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to accept invitation");
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-[var(--bg-canvas)] flex items-center justify-center p-6" data-testid="accept-invite-page">
      <div className="w-full max-w-md bg-white border border-[var(--border)] p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck size={22} className="text-emerald-600" />
          <div>
            <div className="text-[11px] font-mono tracking-widest text-[var(--text-muted)]">/ HELLO BY FLOW PLATFORM</div>
            <div className="text-[16px] font-semibold">Activate your staff account</div>
          </div>
        </div>

        {error && <div className="mb-4 text-red-700 text-[13px] p-3 bg-red-50 border border-red-200" data-testid="accept-error">{error}</div>}

        {done ? (
          <div className="text-emerald-700 text-[13px]" data-testid="accept-success">
            Password set. Redirecting to sign-in…
          </div>
        ) : invite ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="text-[11px] font-mono text-[var(--text-muted)] tracking-widest">EMAIL</div>
              <div className="text-[13px] font-semibold" data-testid="invite-email-display">{invite.email}</div>
              <div className="text-[12px] text-[var(--text-muted)]">Invited as {invite.name}</div>
            </div>
            <div>
              <label className="block text-[11px] font-mono text-[var(--text-muted)] tracking-widest mb-1">NEW PASSWORD</label>
              <input type="password" value={pwd1} onChange={(e) => setPwd1(e.target.value)} className="w-full px-3 py-2 border border-[var(--border)]" data-testid="pwd1" />
              <div className="text-[11px] text-[var(--text-muted)] mt-1">Minimum 10 characters.</div>
            </div>
            <div>
              <label className="block text-[11px] font-mono text-[var(--text-muted)] tracking-widest mb-1">CONFIRM</label>
              <input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="w-full px-3 py-2 border border-[var(--border)]" data-testid="pwd2" />
            </div>
            <button type="submit" disabled={busy || !pwd1 || !pwd2} className="w-full px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-[13px] disabled:opacity-50" data-testid="accept-submit">
              {busy ? "Activating…" : "Activate account"}
            </button>
          </form>
        ) : !error ? (
          <div className="text-[13px] text-[var(--text-muted)]">Verifying invitation…</div>
        ) : null}
      </div>
    </div>
  );
}
